import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription, take } from 'rxjs';
import { AppUtilityService } from '../../../shared/services/app-utility.service';
import { FileUpload, FileUploadConfig } from '../../models/file-upload.model';
import { CommonDialogData } from '../../models/dialog.model';
import { FileMetaData, ReferenceFileInfo } from '../../models/run.model';
import { formatFileSize } from '../../utils/file-size.util';
/** A type alias for a function that performs a cancel operation. This function takes no arguments and returns nothing (void). */
type cancelFunction = () => void;
/**
 * FileUploadComponent
 * This component is responsible for managing file uploads. It allows users to select files,
 * validate their types, upload them to the server, cancel ongoing uploads, and track the
 * progress of individual file uploads. It supports uploading both instrument files and
 * reference files.
 */
@Component({
    selector: 'app-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrl: './file-upload.component.scss'
})

export class FileUploadComponent implements OnInit, OnDestroy {

    @Input() config!: FileUploadConfig;

    @Input() runName?: string;

    @Input() uploadedFiles?: string;

    @Output() instrumentFileUploadResponse = new EventEmitter<FileMetaData>();

    @Output() instrumentUploadBatchComplete = new EventEmitter<void>();

    @Output() instrumentKitLotDialogRequest = new EventEmitter<void>();

    @Output() referenceFileUploadResponse = new EventEmitter<ReferenceFileInfo>();

    @ViewChild("fileSelector", { static: false }) fileSelector!: ElementRef;

    selectedFiles = new Array<FileUpload>();
    fileSelectionForm: FormGroup;

    // Subscriptions
    private fileSelectionSub!: Subscription;
    private fileUploadSub!: Subscription;
    public filesAdded: string[] = [];
    /**
     * 
     * @param appUtilityService Service for API calls and utility functions.
     */
    constructor(private appUtilityService: AppUtilityService) {

        this.fileSelectionForm = new FormGroup({
            file_selection: new FormControl()
        });
    }

    /** Exposed for the template, which shows each staged file's size beside its name. */
    readonly formatFileSize = formatFileSize;

    requestKitLotDialog() {
        this.instrumentKitLotDialogRequest.emit();
    }

    /**
     * Returns true only when the panel and run type required for an instrument
     * upload have been selected. Selecting a panel auto-fills the revision, so
     * these two are the fields the upload request actually needs.
     */
    private hasRequiredInstrumentSelections(): boolean {
        return !!this.config?.panel_name?.trim() && !!this.config?.workflowType?.trim();
    }

    /** Explains to the user why an instrument upload cannot proceed yet. */
    private promptMissingInstrumentSelections(): void {
        this.appUtilityService.displayAlertDialog({
            title: 'Selection Required',
            message: 'Please select a panel, revision, and run type before uploading instrument files.',
            okText: 'OK',
            dismissText: '',
            okAction: () => { },
            dismissAction: () => { }
        } as CommonDialogData);
    }

    private instrumentBatchInProgress = false;
    private instrumentBatchPending = 0;
    /** ngOnInit lifecycle hook to initiate file tracking when the component is initialized. */
    ngOnInit(): void {
        this.trackFileSelection();
    }
    /** Opens the file selector dialog when invoked. */
    openFileSelector() {
        const fileSelection = this.fileSelector.nativeElement;
        fileSelection.click();
    }
    /** Subscribes to file selection changes and tracks selected files. */
    trackFileSelection() {
        const sub = this.fileSelectionForm.get('file_selection')?.valueChanges.subscribe(
            () => {
                const fileSelection = this.fileSelector.nativeElement;
                this.selectFiles(fileSelection.files);
                this.fileSelector.nativeElement.value = '';
            }
        );
        if (sub) {
            this.fileSelectionSub = sub;
        }
    }
    /** Processes the selected files and adds them to the `selectedFiles` array. */
    selectFiles(incomingFiles: FileList) {
        const incomingFileCount = incomingFiles.length;
        let incorrectMimeType = false;
        for (let i = 0; i < incomingFileCount; i++) {
            const incomingFile = incomingFiles[i];
            // Checking if file has already been added
            if (this.filesAdded.indexOf(incomingFile.name) >= 0) {
                continue;
            }
            // Checking if file type is acceptable
            if (!this.config.mimeTypesAccepted || this.config.mimeTypesAccepted.indexOf(incomingFile.type) >= 0) {
                const selectedFile: FileUpload = {
                    file: incomingFile,
                    isUploadInProgress: false,
                    uploadResult: null,
                    isPatient: incomingFile.name.startsWith('Patient')
                };
                this.selectedFiles.push(selectedFile);
                this.filesAdded.push(selectedFile.file.name);

            } else {
                incorrectMimeType = true;
            }
        }
        // Display error
        if (incorrectMimeType) {
            const message = "Only files of the following types are allowed: " + this.config.mimeTypesAccepted;
            this.appUtilityService.showSnackbar(message);
        }
    }
    /** Handles the upload of a single instrument file by making a request to the server. */
    uploadInstrumentFile(index: number, uploadedFilesOverride?: string): Promise<boolean> {
        if (!this.hasRequiredInstrumentSelections()) {
            this.promptMissingInstrumentSelections();
            return Promise.resolve(false);
        }
        const appUtilityService = this.appUtilityService;
        const fileForUpload = this.selectedFiles[index];

        const formData = new FormData();
        const fileMetaData = this.instrumentFileUploadResponse;
        formData.append('file', fileForUpload.file);

        const uploadedFilesValue = uploadedFilesOverride ?? this.uploadedFiles;
        const runNameParam = this.runName ? '&run_name=' + encodeURIComponent(this.runName) : '';
        const uploadedFilesParam = uploadedFilesValue != null ? '&uploadedFiles=' + encodeURIComponent(uploadedFilesValue) : '';
        const apiRoute = this.config.api + "?panel_name=" + this.config.panel_name + "&workflow=" + this.config.workflowType + runNameParam + uploadedFilesParam;

        fileForUpload.isUploadInProgress = true;
        fileForUpload.uploadResult = null;

        const finalizeUpload = (setSuccess = false) => {
            fileForUpload.isUploadInProgress = false;
            if (setSuccess && fileForUpload.uploadResult == null) {
                fileForUpload.uploadResult = 'success';
            }
            this.decrementInstrumentBatchCounter();
        };

        return new Promise((resolve) => {
            this.appUtilityService.post<FormData, FileMetaData>(apiRoute, formData)
                .pipe(take(1))
                .subscribe({
                    next: (response: FileMetaData) => {
                        if (fileForUpload.file.name.indexOf('error') >= 0) {
                            fileForUpload.uploadResult = appUtilityService.errorMessages.serviceFailure;
                        } else {
                            fileForUpload.uploadResult = 'success';
                        }
                        fileMetaData.emit(response);
                        if (fileForUpload.uploadResult === 'success' && !this.instrumentBatchInProgress) {
                            this.instrumentKitLotDialogRequest.emit();
                        }
                        finalizeUpload(true);
                        resolve(fileForUpload.uploadResult === 'success');
                    },
                    error: (err: Error) => {
                        fileForUpload.uploadResult = appUtilityService.getError(err).message;
                        finalizeUpload();
                        resolve(false);
                    }
                });
        });
    }
    /** Uploads all selected instrument files sequentially. */
    async uploadAllInstrumentFiles() {
        if (!this.hasRequiredInstrumentSelections()) {
            this.promptMissingInstrumentSelections();
            return;
        }
        const selectedFileCount = this.selectedFiles.length;
        const pendingPatientIndexes: number[] = [];
        const pendingOtherIndexes: number[] = [];
        for (let i = 0; i < selectedFileCount; i++) {
            const selectedFile = this.selectedFiles[i];
            if (!selectedFile.isUploadInProgress && selectedFile.uploadResult != 'success') {
                if (selectedFile.isPatient) {
                    pendingPatientIndexes.push(i);
                } else {
                    pendingOtherIndexes.push(i);
                }
            }
        }
        const pendingIndexes = [...pendingPatientIndexes, ...pendingOtherIndexes];
        if (!pendingIndexes.length) {
            return;
        }
        this.instrumentBatchPending = pendingIndexes.length;
        this.instrumentBatchInProgress = true;
        const accumulatedFiles: string[] = JSON.parse(this.uploadedFiles ?? '[]');
        for (const index of pendingIndexes) {
            const success = await this.uploadInstrumentFile(index, JSON.stringify(accumulatedFiles));
            if (success) {
                accumulatedFiles.push(this.selectedFiles[index].file.name);
            }
        }
    }
    /** Uploads a single reference file and returns a promise. */
    uploadReferenceFile(index: number): Promise<void> {
        return new Promise((resolve) => {
            const file = this.selectedFiles[index];

            if (!file || file.uploadResult === 'success' || file.isUploadInProgress) {
                return resolve();
            }
            file.isUploadInProgress = true;
            file.uploadResult = null;
            const formData = new FormData();
            formData.append('file', file.file);

            this.appUtilityService.post<FormData, ReferenceFileInfo>(this.config.api, formData)
                .pipe(take(1))
                .subscribe({
                    next: (response) => {
                        if (file.file.name.includes('error')) {
                            file.uploadResult = this.appUtilityService.errorMessages.serviceFailure;
                        } else {
                            file.uploadResult = 'success';
                            this.referenceFileUploadResponse.emit(response);
                        }
                        file.isUploadInProgress = false;
                        resolve();
                    },
                    error: (err) => {
                        file.uploadResult = this.appUtilityService.getError(err).message;
                        file.isUploadInProgress = false;
                        resolve();
                    }
                });
        });
    }
    /** Uploads all reference files sequentially. */
    async uploadAllReferenceFiles() {
        for (let i = 0; i < this.selectedFiles.length; i++) {
            const file = this.selectedFiles[i];

            if (!file.isUploadInProgress && file.uploadResult !== 'success') {
                await this.uploadReferenceFile(i);
            }
        }
    }
    /** Initiates the file cancel process, displaying a confirmation dialog if upload is in progress. */
    inititateFileCancel(index: number) {
        const file_for_upload = this.selectedFiles[index];
        if (file_for_upload.isUploadInProgress) {
            this.displayFileUploadAbortConfirmation(
                () => {
                    this.cancelFile(index);
                }
            );
        } else {
            this.cancelFile(index);
        }
    }
    /** Displays a confirmation dialog before aborting a file upload. */
    displayFileUploadAbortConfirmation(cancelFunction: cancelFunction) {
        this.appUtilityService.displayAlertDialog(
            {
                title: "Abort File Upload?",
                message: "Upload is already in progress. Aborting now might lead to data inconsistencies.",
                okText: 'Dismiss',
                actionText: 'Abort',
                okAction: () => {
                    cancelFunction();
                }
            } as CommonDialogData);
    }
    /** Cancels a specific file upload by removing it from the list. */
    cancelFile(index: number) {
        this.filesAdded.splice(this.filesAdded.indexOf(this.selectedFiles[index].file.name), 1);
        this.selectedFiles.splice(index, 1);
    }
    /** Cancels all selected file uploads. */
    initiateCancelAll() {
        const selected_file_count = this.selectedFiles.length;
        let is_any_file_being_uploaded = false;
        for (let i = 0; i < selected_file_count; i++) {
            const selected_file = this.selectedFiles[i];
            // Checking if the file is being uploaded
            if (selected_file.isUploadInProgress) {
                is_any_file_being_uploaded = true;
                break;
            }
        }
        if (is_any_file_being_uploaded) {
            this.displayFileUploadAbortConfirmation(
                () => {
                    this.cancelAll();
                }
            );
        } else {
            this.cancelAll();
        }
    }
    /** Cancels all selected files. */
    cancelAll() {
        this.appUtilityService.scrollToElement('.bpx-file-uploader', 100);
        const selectedFileCount = this.selectedFiles.length;
        for (let i = 0; i < selectedFileCount; i++) {
            this.selectedFiles.splice(0, 1);
        }
        this.filesAdded = [];
    }
    /** Checks if any file is not yet uploaded. */
    isAnyFileNotUploaded() {
        const selectedFileCount = this.selectedFiles.length;
        let isAnyFileNotUploaded = false;
        for (let i = 0; i < selectedFileCount; i++) {
            const selectedFile = this.selectedFiles[i];
            // Checking if the file can be uploaded
            if (!selectedFile.isUploadInProgress && selectedFile.uploadResult != 'success') {
                isAnyFileNotUploaded = true;
                break;
            }
        }
        return isAnyFileNotUploaded;
    }

    public isAnyInstrumentUploadInProgress(): boolean {
        if (this.config?.isReferenceFileUpload) {
            return false;
        }
        return this.selectedFiles.some(file => file.isUploadInProgress);
    }

    private decrementInstrumentBatchCounter(): void {
        if (!this.instrumentBatchInProgress) {
            return;
        }
        this.instrumentBatchPending = Math.max(0, this.instrumentBatchPending - 1);
        if (this.instrumentBatchPending === 0) {
            this.instrumentBatchInProgress = false;
            this.instrumentUploadBatchComplete.emit();
        }
    }

/** ngOnDestroy lifecycle hook for cleanup. */
    ngOnDestroy(): void {
        this.appUtilityService.unsubscribeAll([
            this.fileSelectionSub,
            this.fileUploadSub
        ]);
    }
}
